import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Input, Table } from "antd";
import type { InputRef } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AppButton, AppTable } from "components/antd-ui";
import { TableLayout } from "components/table";
import { InputNumber } from "components/Input/InputNumber";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { formatMoney, formatQuantity } from "../opportunity.meta";
import "./ProductTable.scss";

export type ProductRow = {
  key: string;
  id?: number;
  description: string;
  unit: string;
  quantity: number | null;
  price: number | null;
};

type ProductField = "description" | "unit" | "quantity" | "price";

const PRODUCT_FIELDS: ProductField[] = ["description", "unit", "quantity", "price"];

type EditingCell = {
  key: string;
  field: ProductField;
};

type Patch = (key: string, patch: Partial<ProductRow>) => void;

type CellEditProps = {
  editing: boolean;
  onStart: () => void;
  onStop: () => void;
  onTab: (backward: boolean) => void;
};

const stepCell = (rows: ProductRow[], current: EditingCell, delta: 1 | -1): EditingCell | null => {
  const rowIndex = rows.findIndex((row) => row.key === current.key);
  const fieldIndex = PRODUCT_FIELDS.indexOf(current.field);
  if (rowIndex < 0 || fieldIndex < 0) return null;

  let nextRow = rowIndex;
  let nextField = fieldIndex + delta;
  if (nextField >= PRODUCT_FIELDS.length) {
    nextRow += 1;
    nextField = 0;
  } else if (nextField < 0) {
    nextRow -= 1;
    nextField = PRODUCT_FIELDS.length - 1;
  }

  if (nextRow < 0 || nextRow >= rows.length) return null;
  return { key: rows[nextRow].key, field: PRODUCT_FIELDS[nextField] };
};

type ProductTableProps = {
  rows: ProductRow[];
  readOnly?: boolean;
  onChange: (rows: ProductRow[]) => void;
};

const ClickEditText = memo(
  ({
    value,
    readOnly,
    editing,
    onStart,
    onStop,
    onTab,
    onChange,
  }: {
    value: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
  } & CellEditProps) => {
    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
      if (editing) {
        inputRef.current?.focus();
      }
    }, [editing]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Tab") return;
        event.preventDefault();
        onTab(event.shiftKey);
      },
      [onTab],
    );

    if (readOnly || !editing) {
      return (
        <button type="button" className="clickEditCell" onClick={onStart} disabled={readOnly}>
          {value || "\u00a0"}
        </button>
      );
    }

    return (
      <Input
        ref={inputRef}
        className="clickEditCell__input"
        value={value}
        onChange={handleChange}
        onBlur={onStop}
        onPressEnter={onStop}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

ClickEditText.displayName = "ClickEditText";

const ClickEditNumber = memo(
  ({
    value,
    readOnly,
    editing,
    onStart,
    onStop,
    onTab,
    onChange,
  }: {
    value: number | null;
    readOnly?: boolean;
    onChange: (value: number | null) => void;
  } & CellEditProps) => {
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (editing) {
        wrapRef.current?.querySelector("input")?.focus();
      }
    }, [editing]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.currentTarget.value.replaceAll(".", "");
        if (raw === "") {
          onChange(null);
          return;
        }
        const numeric = Number(raw);
        onChange(Number.isNaN(numeric) ? null : numeric);
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Tab") return;
        event.preventDefault();
        onTab(event.shiftKey);
      },
      [onTab],
    );

    if (readOnly || !editing) {
      return (
        <button type="button" className="clickEditCell isRight" onClick={onStart} disabled={readOnly}>
          {value === null ? "\u00a0" : formatQuantity(value)}
        </button>
      );
    }

    return (
      <div ref={wrapRef}>
        <InputNumber
          className="clickEditCell__input"
          value={value ?? ""}
          onChange={handleChange}
          onBlur={onStop}
          onPressEnter={onStop}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  },
);

ClickEditNumber.displayName = "ClickEditNumber";

type FieldCellProps = {
  row: ProductRow;
  readOnly?: boolean;
  onPatch: Patch;
} & CellEditProps;

const DescriptionCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: string) => onPatch(row.key, { description: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditText
      value={row.description}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
DescriptionCell.displayName = "DescriptionCell";

const UnitCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback((value: string) => onPatch(row.key, { unit: value }), [onPatch, row.key]);
  return (
    <ClickEditText
      value={row.unit}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
UnitCell.displayName = "UnitCell";

const QuantityCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: number | null) => onPatch(row.key, { quantity: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditNumber
      value={row.quantity}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
QuantityCell.displayName = "QuantityCell";

const PriceCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: number | null) => onPatch(row.key, { price: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditNumber
      value={row.price}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
PriceCell.displayName = "PriceCell";

const RemoveCell = memo(
  ({ row, onRemove }: { row: ProductRow; onRemove: (key: string) => void }) => {
    const remove = useCallback(() => onRemove(row.key), [onRemove, row.key]);
    return (
      <button type="button" className="productTable__remove" onClick={remove} aria-label="Xóa dòng">
        <DeleteOutlined />
      </button>
    );
  },
);
RemoveCell.displayName = "RemoveCell";

const AmountCell = memo(({ row }: { row: ProductRow }) => {
  const amount = (row.quantity || 0) * (row.price || 0);
  return <span className="productTable__amount">{formatMoney(amount)}</span>;
});
AmountCell.displayName = "AmountCell";

export const createProductRow = (): ProductRow => ({
  key: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  description: "",
  unit: "",
  quantity: null,
  price: null,
});

const isEditing = (editingCell: EditingCell | null, key: string, field: ProductField) =>
  editingCell?.key === key && editingCell.field === field;

export const ProductTable = memo(({ rows, readOnly, onChange }: ProductTableProps) => {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const addWrapRef = useRef<HTMLDivElement>(null);
  const focusAddRef = useRef(false);

  const onPatch = useCallback(
    (key: string, patch: Partial<ProductRow>) => {
      onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    },
    [onChange, rows],
  );

  const onRemove = useCallback(
    (key: string) => {
      setEditingCell((current) => (current?.key === key ? null : current));
      const next = rows.filter((row) => row.key !== key);
      onChange(next.length ? next : [createProductRow()]);
    },
    [onChange, rows],
  );

  const addRow = useCallback(() => {
    onChange([...rows, createProductRow()]);
  }, [onChange, rows]);

  const startEdit = useCallback(
    (key: string, field: ProductField) => {
      if (readOnly) return;
      setEditingCell({ key, field });
    },
    [readOnly],
  );

  const stopEdit = useCallback((key: string, field: ProductField) => {
    setEditingCell((current) => (current?.key === key && current.field === field ? null : current));
  }, []);

  const moveEdit = useCallback(
    (key: string, field: ProductField, backward: boolean) => {
      const next = stepCell(rows, { key, field }, backward ? -1 : 1);
      if (next) {
        setEditingCell(next);
        return;
      }
      focusAddRef.current = !backward;
      setEditingCell(null);
    },
    [rows],
  );

  useEffect(() => {
    if (editingCell !== null || !focusAddRef.current) return;
    focusAddRef.current = false;
    addWrapRef.current?.querySelector("button")?.focus();
  }, [editingCell]);

  const sumQuantity = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
  const sumAmount = rows.reduce((sum, row) => sum + (row.quantity || 0) * (row.price || 0), 0);

  const columns = useMemo<ColumnsType<ProductRow>>(() => {
    const next: ColumnsType<ProductRow> = [
      {
        title: "Diễn giải",
        dataIndex: "description",
        sorter: false,
        render: (_, row) => (
          <DescriptionCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "description")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "description")}
            onStop={() => stopEdit(row.key, "description")}
            onTab={(backward) => moveEdit(row.key, "description", backward)}
          />
        ),
      },
      {
        title: "ĐVT",
        dataIndex: "unit",
        width: 120,
        sorter: false,
        render: (_, row) => (
          <UnitCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "unit")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "unit")}
            onStop={() => stopEdit(row.key, "unit")}
            onTab={(backward) => moveEdit(row.key, "unit", backward)}
          />
        ),
      },
      {
        title: "Số lượng",
        dataIndex: "quantity",
        width: 140,
        align: "right",
        sorter: false,
        render: (_, row) => (
          <QuantityCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "quantity")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "quantity")}
            onStop={() => stopEdit(row.key, "quantity")}
            onTab={(backward) => moveEdit(row.key, "quantity", backward)}
          />
        ),
      },
      {
        title: "Đơn giá",
        dataIndex: "price",
        width: 160,
        align: "right",
        sorter: false,
        render: (_, row) => (
          <PriceCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "price")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "price")}
            onStop={() => stopEdit(row.key, "price")}
            onTab={(backward) => moveEdit(row.key, "price", backward)}
          />
        ),
      },
      {
        title: "Thành tiền",
        key: "amount",
        width: 160,
        align: "right",
        render: (_, row) => <AmountCell row={row} />,
      },
    ];

    if (!readOnly) {
      next.push({
        title: "",
        key: "remove",
        width: 56,
        align: "center",
        render: (_, row) => <RemoveCell row={row} onRemove={onRemove} />,
      });
    }

    return next;
  }, [editingCell, moveEdit, onPatch, onRemove, readOnly, startEdit, stopEdit]);

  const summary = useCallback(
    () => (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={2}>
          <span className="productTable__totalLabel">Tổng cộng</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          {formatQuantity(sumQuantity)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} />
        <Table.Summary.Cell index={4} align="right">
          {formatMoney(sumAmount)}
        </Table.Summary.Cell>
        {!readOnly && <Table.Summary.Cell index={5} />}
      </Table.Summary.Row>
    ),
    [readOnly, sumAmount, sumQuantity],
  );

  return (
    <div className="productTable">
      <TableLayout
        table={
          <AppTable<ProductRow>
            rowKey="key"
            pagination={false}
            dataSource={rows}
            columns={columns}
            summary={summary}
          />
        }
      />
      {!readOnly && (
        <div ref={addWrapRef}>
          <AppButton className="productTable__add" icon={<PlusOutlined />} onClick={addRow}>
            Thêm dòng
          </AppButton>
        </div>
      )}
    </div>
  );
});

ProductTable.displayName = "ProductTable";
